import clsx from 'clsx';
import { useRef, DetailedHTMLProps, IframeHTMLAttributes, useEffect, useState } from 'react';

import styles from './iframe.module.scss';
import { useWindowSize } from 'utils/use-window-size';

type HTMLIframeElementProps = DetailedHTMLProps<IframeHTMLAttributes<HTMLIFrameElement>, HTMLIFrameElement>;

type IFrameAutoHeightProps = {
	autoHeight: true;
	height: number;
};

type IFrameFixedHeightProps = {
	autoHeight?: false;
	height?: number;
};

export type IFrameProps = HTMLIframeElementProps &
	(IFrameAutoHeightProps | IFrameFixedHeightProps) & {
		borderless?: boolean;
		fullWidth?: boolean;
		disableAutoHeightOnMobile?: boolean;
	};

const IFrame = ({
	style,
	height,
	borderless = true,
	fullWidth = true,
	autoHeight = false,
	disableAutoHeightOnMobile = false,
	...rest
}: IFrameProps): React.JSX.Element => {
	const iframeRef = useRef<HTMLIFrameElement>(null);
	const [contentHeight, setContentHeight] = useState<number | undefined>(height);

	const { width } = useWindowSize(200);
	const isMobile = width ? width < 768 : false;

	// Determine if autoHeight should be applied based on mobile detection
	const shouldUseAutoHeight = autoHeight && !(disableAutoHeightOnMobile && isMobile);

	const heightStyles = height
		? {
				height: contentHeight
		  }
		: {};

	useEffect(() => {
		if (!shouldUseAutoHeight || !iframeRef?.current?.contentWindow) return;

		try {
			if (iframeRef?.current?.contentWindow?.document?.body?.scrollHeight) {
				setContentHeight(iframeRef.current.contentWindow.document.body.scrollHeight);
			}
		} catch {
			// eslint-disable-next-line no-console
			console.error("Can't access iframe content (CORS)");
		}
	}, [iframeRef?.current?.contentWindow, shouldUseAutoHeight]);

	return (
		<iframe
			{...rest}
			ref={iframeRef}
			className={clsx(borderless && styles.borderless, fullWidth && styles.fullWidth)}
			style={{ ...style, ...heightStyles }}
		/>
	);
};

export default IFrame;
